import {
  MeasureDataSplitDetails,
  MeasureDataTableRow,
  NumericFormatUtil,
  WideCell,
  WideMeasureItem,
  WideRow
} from "store/types";

import { currencySign } from "../../../../constants";

export function formatCellValue(row: MeasureDataTableRow, formatNum: NumericFormatUtil): string {
  const raw = row.value ?? "";
  const num = Number(raw);
  if (isNaN(num)) return raw;
  if (row.postfix === "%") return formatNum({ value: `${num}%`, format: { decimal: 2 } });
  if (row.is_currency && row.currency)
    return formatNum({ value: `${currencySign[row.currency]}${num}`, format: { decimal: 2 } });
  if (row.postfix) return `${row.postfix}${formatNum({ value: num, format: { decimal: 2 } })}`;
  return formatNum({ value: num, format: { decimal: 2 } });
}

function toWideMeasureItem(row: MeasureDataTableRow): WideMeasureItem {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const r = row as any; // runtime object has IMeasureData fields not in the TS type
  return {
    id: row.id,
    value: row.value ?? "",
    measure: row.parameter,
    measure_id: r.measure_id ?? null,
    is_negative: r.is_negative ?? false,
    investor_id: r.investor_id ?? null,
    investor_name: row.investor || null,
    round_id: r.round_id ?? null,
    round_name: row.round ?? null,
    deal_id: row.deal_id,
    date: row.date,
    accounting_date: row.accounting_date ?? null,
    effective_range_from: row.effective_range_from ?? null,
    effective_range_to: row.effective_range_to ?? null,
    currency: row.currency ?? null,
    description: row.description ?? null,
    split_id: row.split_id ?? null,
    batch_id: row.batch_id ?? null,
    files: r.investor_files?.length ? r.investor_files : null
  };
}

/**
 * Pivots flat monitoring rows (showTopInvestors: true) into wide rows.
 *
 * Each top-level row = one measure for a date/round (aggregated).
 * Its `.children` = per-investor splits for that measure.
 *
 * Wide row = one (date, round) group.
 *   Parent row  → aggregated values from the top-level rows.
 *   Child rows  → one per investor, values collected across all measures.
 */
export function buildWideData(
  topLevelRows: MeasureDataTableRow[],
  formatNum: NumericFormatUtil
): {
  wideRows: WideRow[];
  measureColumns: string[];
} {
  // Collect all measure names preserving first-seen order (top-level + children)
  const measureOrder: string[] = [];
  const measureSeen = new Set<string>();
  const addMeasure = (name: string) => {
    if (!measureSeen.has(name)) {
      measureSeen.add(name);
      measureOrder.push(name);
    }
  };
  topLevelRows.forEach((r) => {
    addMeasure(r.parameter);
    r.children?.forEach((c) => addMeasure(c.parameter));
  });

  // Group top-level rows by (deal, date, round) — including deal so that global
  // context correctly separates same-date/round entries from different deals.
  const groupMap = new Map<string, MeasureDataTableRow[]>();
  topLevelRows.forEach((row) => {
    const dateKey = row.date ? row.date.slice(0, 10) : "";
    const groupKey = `${row.deal ?? ""}||${dateKey}||${row.round ?? ""}`;
    if (!groupMap.has(groupKey)) groupMap.set(groupKey, []);
    groupMap.get(groupKey)!.push(row);
  });

  const wideRows: WideRow[] = [];

  groupMap.forEach((groupRows, groupKey) => {
    const firstRow = groupRows[0];
    const date = firstRow.date ? firstRow.date.slice(0, 10) : "";
    const round = firstRow.round ?? "—";
    const deal = firstRow.deal ?? null;
    const dealId = firstRow.deal_id ?? null;

    const anyHasChildren = groupRows.some((r) => r.children && r.children.length > 0);

    if (anyHasChildren) {
      // ── Rows have investor children (showTopInvestors: true response) ────────

      const parentCells: Record<string, WideCell | null> = {};
      const parentCellsAccum: Record<string, { sum: number; sampleRow: MeasureDataTableRow; count: number }> = {};
      const allDataIds: number[] = [];
      const allBatchIds = new Set<number>();

      type InvAccum = {
        cells: Record<string, WideCell | null>;
        dataIds: number[];
        batchIds: Set<number>;
        investor: string;
        bankAccount: string | null;
        measureItems: WideMeasureItem[];
      };
      const investorMap = new Map<string, InvAccum>();

      groupRows.forEach((row) => {
        const hasChildren = !!row.children?.length;
        if (!parentCellsAccum[row.parameter]) {
          parentCellsAccum[row.parameter] = { sum: 0, sampleRow: row, count: 0 };
        }
        parentCellsAccum[row.parameter].sum += Number(row.value ?? 0);
        parentCellsAccum[row.parameter].count++;
        if (row.id && hasChildren) allDataIds.push(row.id);
        if (row.batch_id && hasChildren) allBatchIds.add(row.batch_id);

        const childSources: MeasureDataTableRow[] = hasChildren ? row.children! : row.investor ? [row] : [];
        childSources.forEach((child) => {
          const invName = child.investor || "—";
          const bankId = (child as any).bank_detail_id ?? null;
          const bankName = (child as any).bank_detail_name ?? null;
          const invKey = `${invName}__${bankId ?? ""}`;

          if (!investorMap.has(invKey)) {
            investorMap.set(invKey, {
              cells: {},
              dataIds: [],
              batchIds: new Set(),
              investor: invName,
              bankAccount: bankName,
              measureItems: []
            });
          }
          const acc = investorMap.get(invKey)!;
          acc.cells[row.parameter] = { displayValue: formatCellValue(child, formatNum), dataId: child.id };
          if (child.id) {
            acc.dataIds.push(child.id);
            allDataIds.push(child.id);
          }
          if (child.batch_id) {
            acc.batchIds.add(child.batch_id);
            allBatchIds.add(child.batch_id);
          }
          acc.measureItems.push(toWideMeasureItem(child));
        });
      });

      Object.entries(parentCellsAccum).forEach(([param, { sum, sampleRow, count }]) => {
        parentCells[param] = {
          displayValue: formatCellValue({ ...sampleRow, value: sum.toString() }, formatNum),
          dataId: count === 1 ? sampleRow.id : -1
        };
      });

      const allMeasureItems: WideMeasureItem[] = [];
      const children: WideRow[] = [];
      investorMap.forEach((acc, invKey) => {
        allMeasureItems.push(...acc.measureItems);
        children.push({
          key: `${groupKey}_${invKey}`,
          batchIds: Array.from(acc.batchIds),
          date,
          round,
          deal,
          dealId,
          investor: acc.investor !== "—" ? acc.investor : null,
          bankAccount: acc.bankAccount,
          cells: acc.cells,
          children: [],
          allDataIds: acc.dataIds,
          isAggregated: false,
          measureItems: acc.measureItems
        });
      });

      wideRows.push({
        key: `${groupKey}_agg`,
        batchIds: Array.from(allBatchIds),
        date,
        round,
        deal,
        dealId,
        investor: null,
        bankAccount: null,
        cells: parentCells,
        children,
        allDataIds,
        isAggregated: true,
        measureItems: allMeasureItems
      });
    } else {
      // ── Flat rows (no investor children) ─────────────────────────────────────

      const flatMap = new Map<string, { rows: MeasureDataTableRow[]; investor: string; bankAccount: string | null }>();
      groupRows.forEach((row) => {
        const invName = row.investor || "—";
        const bankId = (row as any).bank_detail_id ?? null;
        const bankName = (row as any).bank_detail_name ?? null;
        const key = `${invName}__${bankId ?? ""}`;
        if (!flatMap.has(key)) flatMap.set(key, { rows: [], investor: invName, bankAccount: bankName });
        flatMap.get(key)!.rows.push(row);
      });

      const investorKeys = Array.from(flatMap.keys());

      if (investorKeys.length === 1) {
        const entry = flatMap.get(investorKeys[0])!;
        const invRows = entry.rows;
        const cells: Record<string, WideCell | null> = {};
        const batchIds = new Set<number>();
        invRows.forEach((r) => {
          cells[r.parameter] = { displayValue: formatCellValue(r, formatNum), dataId: r.id };
          if (r.batch_id) batchIds.add(r.batch_id);
        });
        const investor = entry.investor !== "—" ? entry.investor : null;
        wideRows.push({
          key: `${groupKey}_${investorKeys[0]}`,
          batchIds: Array.from(batchIds),
          date,
          round,
          deal,
          dealId,
          investor,
          bankAccount: entry.bankAccount,
          cells,
          children: [],
          allDataIds: invRows.map((r) => r.id).filter(Boolean) as number[],
          isAggregated: false,
          measureItems: invRows.map(toWideMeasureItem)
        });
      } else {
        // Multiple (investor, bank) combinations — build parent + children
        const children: WideRow[] = [];
        const allDataIds: number[] = [];
        const allBatchIds = new Set<number>();
        const parentCellsRaw: Record<string, { sum: number; sampleRow: MeasureDataTableRow }> = {};
        const allMeasureItems: WideMeasureItem[] = [];

        flatMap.forEach((entry, invKey) => {
          const invRows = entry.rows;
          const invName = entry.investor;
          const cells: Record<string, WideCell | null> = {};
          const invBatchIds = new Set<number>();
          const invMeasureItems: WideMeasureItem[] = [];
          invRows.forEach((r) => {
            cells[r.parameter] = { displayValue: formatCellValue(r, formatNum), dataId: r.id };
            if (r.id) allDataIds.push(r.id);
            if (r.batch_id) {
              allBatchIds.add(r.batch_id);
              invBatchIds.add(r.batch_id);
            }
            if (!parentCellsRaw[r.parameter]) {
              parentCellsRaw[r.parameter] = { sum: 0, sampleRow: r };
            }
            parentCellsRaw[r.parameter].sum += Number(r.value ?? 0);
            invMeasureItems.push(toWideMeasureItem(r));
          });
          allMeasureItems.push(...invMeasureItems);
          children.push({
            key: `${groupKey}_${invKey}`,
            batchIds: Array.from(invBatchIds),
            date,
            round,
            deal,
            dealId,
            investor: invName !== "—" ? invName : null,
            bankAccount: entry.bankAccount,
            cells,
            children: [],
            allDataIds: invRows.map((r) => r.id).filter(Boolean) as number[],
            isAggregated: false,
            measureItems: invMeasureItems
          });
        });

        const parentCells: Record<string, WideCell | null> = {};
        Object.entries(parentCellsRaw).forEach(([measure, { sum, sampleRow }]) => {
          parentCells[measure] = {
            displayValue: formatCellValue({ ...sampleRow, value: sum.toString() }, formatNum),
            dataId: -1
          };
        });

        wideRows.push({
          key: `${groupKey}_agg`,
          batchIds: Array.from(allBatchIds),
          date,
          round,
          deal,
          dealId,
          investor: null,
          bankAccount: null,
          cells: parentCells,
          children,
          allDataIds,
          isAggregated: true,
          measureItems: allMeasureItems
        });
      }
    }
  });

  wideRows.sort((a, b) => b.date.localeCompare(a.date));

  return { wideRows, measureColumns: measureOrder };
}

/**
 * Merge split details from multiple batches into one object for GroupsForm.
 * ui_group_id is remapped to be globally unique so each measure gets its own group.
 */
export function mergeSplitDetails(details: MeasureDataSplitDetails[]): MeasureDataSplitDetails {
  if (details.length <= 1) return details[0] ?? { deal_rounds: {}, splits: {} };

  const merged: MeasureDataSplitDetails = { deal_rounds: {}, splits: {} };
  let nextGroupId = 1;

  details.forEach((detail) => {
    // Merge deal_rounds — keep first occurrence per deal
    Object.entries(detail.deal_rounds).forEach(([dealId, rounds]) => {
      const key = Number(dealId);
      if (!merged.deal_rounds[key]) merged.deal_rounds[key] = rounds;
    });

    // Add each split group with a unique ui_group_id
    Object.values(detail.splits).forEach((splitsArray) => {
      const groupId = nextGroupId++;
      const recordKey = splitsArray[0]?.id ?? groupId;
      merged.splits[recordKey] = splitsArray.map((s) => ({ ...s, ui_group_id: groupId }));
    });
  });

  return merged;
}

/**
 * Keep only split groups where at least one monitoring_data entry has an ID in `dataIds`.
 * Used to narrow the edit drawer to a single clicked measure column.
 */
export function filterSplitDetailsByDataIds(
  splitDetails: MeasureDataSplitDetails,
  dataIds: Set<number>
): MeasureDataSplitDetails {
  const filtered: MeasureDataSplitDetails = {
    deal_rounds: splitDetails.deal_rounds,
    splits: {}
  };
  Object.entries(splitDetails.splits).forEach(([key, splits]) => {
    const matching = splits.filter((split) => split.monitoring_data.some((item) => dataIds.has(item.id)));
    if (matching.length > 0) filtered.splits[Number(key)] = matching;
  });
  return filtered;
}

export function filterSplitDetailsByInvestor(
  splitDetails: MeasureDataSplitDetails,
  investorName: string
): MeasureDataSplitDetails {
  const filtered: MeasureDataSplitDetails = {
    deal_rounds: splitDetails.deal_rounds,
    splits: {}
  };
  Object.entries(splitDetails.splits).forEach(([key, splits]) => {
    const filteredSplits = splits
      .map((split) => ({
        ...split,
        monitoring_data: split.monitoring_data.filter((item) => item.investor?.name === investorName)
      }))
      .filter((split) => split.monitoring_data.length > 0);
    if (filteredSplits.length > 0) {
      filtered.splits[Number(key)] = filteredSplits;
    }
  });
  return filtered;
}
